import { CSSObject } from '@emotion/react'
import { DotLoader } from '@opallabs/lapidary'
import useTwitterContentPreview from '../Content/helpers/hooks/useTwitterContentPreview'
import {
  ContentPreviewState,
  ContentPreviewStateReturn,
  GetContentByIDV2Return,
} from '../Content/helpers/types'
import { PreviewDisplay } from './PreviewDisplay'
import { PreviewError } from './PreviewError'
import { PreviewInput } from './PreviewInput'
import { styles } from './styles'

type ContentPreviewOverrideStyle = {
  input: CSSObject
}

interface Props {
  display: string
  embeddedContentType?: string
  embeddedUrl: string
  embeddedUrlRegex: RegExp
  customLoadPreviewHook?: () => ContentPreviewStateReturn
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  isPresenting: boolean
  onEmbeddedUrlChange: (val: string) => void
  onRefreshChange: (val: string) => void
  onSnapshotUrlChange: (val: string) => void
  overrideStyles?: ContentPreviewOverrideStyle
  readOnly: boolean
  refreshedAt: string
  snapshotUrl: string
}

export const ContentPreview: React.FC<Props> = ({
  display,
  embeddedContentType = 'Post',
  embeddedUrl,
  embeddedUrlRegex,
  customLoadPreviewHook = useTwitterContentPreview,
  getContentByIdV2,
  isPresenting,
  onEmbeddedUrlChange,
  onRefreshChange,
  onSnapshotUrlChange,
  overrideStyles,
  readOnly,
  refreshedAt,
  snapshotUrl,
}) => {
  const { embedlyPreview, state } = customLoadPreviewHook({
    embeddedUrl,
    embeddedUrlRegex,
    getContentByIdV2,
    onSnapshotUrlChange,
    snapshotUrl,
  })

  const onStartOver = () => {
    /* Handles reverting all fields to empty values; users cannot
     * interact with start over button from read only views
     */
    if (!(readOnly || isPresenting)) {
      onEmbeddedUrlChange('')
      onSnapshotUrlChange('')
      onRefreshChange('')
    }
  }

  const onRefresh = () => {
    /* Handles refreshing and resetting snapshot url so as to
     * trigger the useEffect in the parent component
     */
    onRefreshChange(new Date().toString())
    onSnapshotUrlChange('snapshot_url')
  }

  return (
    <div>
      {state === ContentPreviewState.EMPTY && (
        <PreviewInput
          overrideStyles={overrideStyles?.input}
          embeddedContentType={embeddedContentType}
          url={embeddedUrl || snapshotUrl}
          onRefreshChange={(val: string) => onRefreshChange(val)}
          onUrlChange={(val: string) => onEmbeddedUrlChange(val)}
        />
      )}
      <div css={styles.getLoaderWrapper(state === ContentPreviewState.LOADING)}>
        {state !== ContentPreviewState.LOADED && <DotLoader />}
      </div>
      {state === ContentPreviewState.LOADED && (
        <PreviewDisplay
          embeddedUrl={embeddedUrl}
          snapshotUrl={snapshotUrl}
          refreshedAt={refreshedAt}
          embedlyPreview={embedlyPreview}
          isPresenting={isPresenting}
          readOnly={readOnly}
          onRefreshChange={(val: string) => onRefreshChange(val)}
          onEmbeddedUrlChange={(val: string) => onEmbeddedUrlChange(val)}
          onSnapshotUrlChange={(val: string) => onSnapshotUrlChange(val)}
        />
      )}
      {state === ContentPreviewState.ERROR && (
        <PreviewError
          embeddedContentType={embeddedContentType}
          display={display}
          isEmbeddedUrl={embeddedUrlRegex.test(embeddedUrl)}
          onStartOver={onStartOver}
          onRefresh={onRefresh}
        />
      )}
    </div>
  )
}
